import React, { useState } from "react";
import { Box, Tooltip } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import ListColumn from "../../../components/Globo/ListColumn/ListColumn";
import GloboIconButton from "../../../components/Globo/Button/IconButton";
import GloboAutocomplete from "../../../components/Globo/Autocomplete/Autocomplete";
import ConfirmDialog from "../../../components/Globo/ConfirmDialog/ConfirmDialog";
import { getUnassociatedResources } from "../../../hooks/usePermissionCatalog";
import {
  associateResourceToProfile,
  disassociateResourceFromProfile,
} from "../../../services/api/maintenance/permissionCatalog.api";
import { toastSuccess, toastError } from "../../../utils/toast";

const extractErrorMessage = (error) => {
  const backendErrors = error?.response?.data;
  if (Array.isArray(backendErrors) && backendErrors.length) {
    return backendErrors
      .map((backendError) => backendError.message)
      .filter(Boolean)
      .join(" ");
  }
  return "Não foi possível concluir a operação.";
};

// Visão "Perfil ↔ Recurso": única visão com escrita - associar/desassociar um recurso a um perfil.
const ProfileResourceView = ({ profiles, resources, patchProfileResources }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProfileName, setSelectedProfileName] = useState(null);
  const [addValue, setAddValue] = useState(null);
  const [pendingRemoval, setPendingRemoval] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSelectProfile = (profileName) => {
    setSelectedProfileName(profileName);
    setAddValue(null);
    setPendingRemoval(null);
  };

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const filteredProfiles = normalizedSearchTerm
    ? profiles.filter((profile) =>
        profile.name.toLowerCase().includes(normalizedSearchTerm)
      )
    : profiles;

  const profileItems = filteredProfiles.map((profile) => ({
    id: profile.name,
    label: profile.name,
    active: profile.isActive,
  }));

  const selectedProfile = selectedProfileName
    ? profiles.find((profile) => profile.name === selectedProfileName)
    : null;

  const resourceItems = (selectedProfile?.resources ?? []).map((name) => ({
    id: name,
    label: name,
  }));

  const unassociatedOptions = selectedProfile
    ? getUnassociatedResources(selectedProfile, resources).map((resource) => ({
        id: resource.name,
        label: resource.name,
      }))
    : [];

  const handleAddResource = async () => {
    if (!addValue || !selectedProfile) return;

    setIsSubmitting(true);
    try {
      await associateResourceToProfile(selectedProfile.name, addValue.id);
      patchProfileResources(selectedProfile.name, [
        ...selectedProfile.resources,
        addValue.id,
      ]);
      toastSuccess(`Recurso "${addValue.id}" associado ao perfil "${selectedProfile.name}".`);
      setAddValue(null);
    } catch (error) {
      toastError(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmRemove = async () => {
    if (!pendingRemoval || !selectedProfile) return;

    const { resourceName } = pendingRemoval;
    setIsSubmitting(true);
    try {
      await disassociateResourceFromProfile(selectedProfile.name, resourceName);
      patchProfileResources(
        selectedProfile.name,
        selectedProfile.resources.filter((name) => name !== resourceName)
      );
      toastSuccess(`Recurso "${resourceName}" removido do perfil "${selectedProfile.name}".`);
    } catch (error) {
      toastError(extractErrorMessage(error));
    } finally {
      setIsSubmitting(false);
      setPendingRemoval(null);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        border: "1px solid #E2E2E3",
        borderRadius: "8px",
        height: "100%",
      }}
    >
      <ListColumn
        title="Perfis"
        items={profileItems}
        selectedId={selectedProfileName}
        onSelect={handleSelectProfile}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Buscar perfil"
        emptyMessage="Nenhum perfil encontrado."
        sx={{ flex: 1, borderRight: "1px solid #E2E2E3" }}
      />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
          minHeight: 0,
        }}
      >
        <ListColumn
          title="Recursos associados"
          items={resourceItems}
          selectedId={null}
          searchable={false}
          emptyMessage={
            selectedProfileName
              ? "Este perfil ainda não tem recursos associados."
              : "Selecione um perfil."
          }
          renderItemAction={(item) => (
            <Tooltip title="Remover recurso do perfil">
              <span>
                <GloboIconButton
                  icon={<DeleteOutlineIcon fontSize="small" />}
                  aria-label={`Remover ${item.label} do perfil`}
                  disabled={isSubmitting}
                  onClick={() => setPendingRemoval({ resourceName: item.id })}
                />
              </span>
            </Tooltip>
          )}
          sx={{ flex: 1 }}
        />

        {selectedProfileName && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: 2,
              borderTop: "1px solid #E2E2E3",
            }}
          >
            <GloboAutocomplete
              options={unassociatedOptions}
              value={addValue}
              onChange={setAddValue}
              placeholder="Adicionar recurso"
              disabled={isSubmitting}
              sx={{ flex: 1 }}
            />
            <Tooltip title="Adicionar recurso ao perfil">
              <span>
                <GloboIconButton
                  icon={<AddIcon fontSize="small" />}
                  aria-label="Adicionar recurso ao perfil"
                  disabled={!addValue || isSubmitting}
                  onClick={handleAddResource}
                />
              </span>
            </Tooltip>
          </Box>
        )}
      </Box>

      <ConfirmDialog
        open={Boolean(pendingRemoval)}
        title="Remover recurso do perfil"
        text={
          pendingRemoval
            ? `Remover o recurso "${pendingRemoval.resourceName}" do perfil "${selectedProfileName}"? Usuários com esse perfil perderão esse acesso.`
            : ""
        }
        confirmLabel="Remover"
        onConfirm={handleConfirmRemove}
        onCancel={() => setPendingRemoval(null)}
      />
    </Box>
  );
};

export default ProfileResourceView;
